/* eslint-disable import/no-extraneous-dependencies */
import React from "react";

import { Formik } from "formik";
import { Box, TextField } from "@mui/material";
import * as Yup from "yup";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router";
import { Bounce, toast } from "react-toastify";

import CustomButton from "../../Button";
import { episodeService } from "../../../Episode/services";
import { Quote } from "../../../Episode/services/episode.service";
import { Category } from "../../types";

export type QuoteObject = {
  speaker: string;
  quote: string;
  timestamp: string;
};

interface Props {
  quoteObject: QuoteObject;
  quotes: Quote[];
  handleCloseModal: () => void;
  refetch: () => void;
  category: Category;
}

function QuoteModal({
  quoteObject,
  handleCloseModal,
  quotes,
  refetch,
  category,
}: Props) {
  const { id: episodeId } = useParams();

  const { mutateAsync, error, isSuccess, isPending } = useMutation({
    mutationKey: ["updateInsights-quote"],
    mutationFn: (values: QuoteObject[]) =>
      episodeService.updateInsights(episodeId, {
        category,
        value: values,
      }),
  });

  const handleQuoteUpdate = async (values: Omit<QuoteObject, "timestamp">) => {
    const updatedQuotes = quotes.map((quote) => {
      if (quote.timestamp === quoteObject.timestamp) {
        return {
          ...quote,
          speaker: values.speaker,
          quote: values.quote,
        };
      }
      return quote;
    });

    await toast.promise(mutateAsync(updatedQuotes), {
      success: {
        toastId: `${quoteObject}-success`,
        render() {
          return "Saved!";
        },
        type: "success",
        theme: "colored",
        autoClose: 2000,
        transition: Bounce,
        delay: 500,
      },
      pending: {
        toastId: `${quoteObject}-pending`,
        render() {
          return "Saving, please wait...";
        },
        type: "default",
        theme: "colored",
        transition: Bounce,
      },
      error: {
        toastId: `${quoteObject}-error`,
        render() {
          return `Error: ${error?.message}`;
        },
        type: "error",
        theme: "colored",
        autoClose: false,
        transition: Bounce,
        delay: 500,
      },
    });
  };

  if (isSuccess) {
    handleCloseModal();
    refetch();
  }

  const formikInitialValues = {
    speaker: quoteObject.speaker,
    quote: quoteObject.quote,
  };
  return (
    <Formik
      initialValues={formikInitialValues}
      validationSchema={Yup.object({
        speaker: Yup.string().required("Required"),
        quote: Yup.string().required("Required"),
      })}
      onSubmit={handleQuoteUpdate}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit}>
          <DialogContent dividers>
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <TextField
                sx={{ width: "70%", alignSelf: "center" }}
                margin="normal"
                label="Speaker"
                error={!!formik.errors.speaker}
                helperText={formik.errors.speaker}
                {...formik.getFieldProps("speaker")}
              />

              <TextField
                sx={{ width: "70%", alignSelf: "center" }}
                margin="normal"
                label="Quote"
                error={!!formik.errors.quote}
                helperText={formik.errors.quote}
                multiline
                {...formik.getFieldProps("quote")}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button autoFocus onClick={handleCloseModal} disabled={isPending}>
              Close
            </Button>
            <CustomButton
              label="Save"
              type="submit"
              variant="contained"
              color="success"
              size="small"
              disabled={!formik.isValid || isPending}
            />
          </DialogActions>
        </form>
      )}
    </Formik>
  );
}

export default QuoteModal;
